/**
 * HostModal — the studio-owned UI for the `openModal` ActionFlow action.
 *
 * The corpus authors only `openModal { popup: <id>, ...props }` and never the
 * modal component itself (see docs/studio/actionflow-host-wiring.md — "the host
 * owns the component set"). The known corpus ids are `contactForm`,
 * `agentContactModal`, and `scheduleTourModal`; the runtime strips `popup` /
 * `modalId` and forwards the remaining params (e.g. `agentId`, `propertyId`) as
 * `props`.
 *
 * This is the GENERIC functional modal: a real Radix dialog (matching studio's
 * dialog conventions — see components/payment/consent-modal.tsx) that renders a
 * humanized title + any forwarded params, so a flow's `openModal` demonstrably
 * opens a dialog in the real build-mode canvas. The studio (UI) session can
 * later replace any id with a bespoke component in the same registry.
 */

import { type ComponentType } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import type { ModalComponentProps, ModalComponentRegistry } from '@cogo/react-renderer-v2'
import {
  hashHostCapabilityContent,
  type HostRegistryHandlerDeclaration,
} from '@cogo/schema/host-capability-manifest'
import { ContactAgentModal } from './modals/contact-agent-modal'
import { ScheduleTourModal } from './modals/schedule-tour-modal'

/** Modal ids the corpus opens today. New ids fall back to the same generic UI. */
export const KNOWN_HOST_MODAL_IDS = ['contactForm', 'agentContactModal', 'scheduleTourModal'] as const

const STUDIO_MODAL_REGISTRY_DESCRIPTOR = {
  id: 'cogo.studio.modal-registry.v1',
  implementation: 'apps/cogo-studio/src/shell/ide-v2/host/host-modal.tsx',
  selector: '*',
  operation: 'open',
} as const

export const STUDIO_MODAL_REGISTRY_REF = {
  ref: 'cogo://studio/host-adapter/modal-registry/v1',
  contentHash: hashHostCapabilityContent(STUDIO_MODAL_REGISTRY_DESCRIPTOR),
} as const

const UNSAFE_MODAL_IDS = new Set(['__proto__', 'prototype', 'constructor'])

const TITLES: Record<string, string> = {
  contactForm: 'Contact',
  agentContactModal: 'Contact agent',
  scheduleTourModal: 'Schedule a tour',
}

function humanize(id: string): string {
  return (
    TITLES[id] ??
    id
      .replace(/[-_]/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/^\w/, (c) => c.toUpperCase())
  )
}

const INTERNAL_KEYS = new Set(['title', 'description', 'message', 'popup', 'modalId'])

/** The generic dialog body, bound to a specific modal id for its title. */
function HostModalBody({ modalId, props, close }: { modalId: string } & ModalComponentProps) {
  const title = typeof props.title === 'string' ? props.title : humanize(modalId)
  const description =
    typeof props.description === 'string'
      ? props.description
      : typeof props.message === 'string'
        ? props.message
        : undefined
  const entries = Object.entries(props).filter(([k]) => !INTERNAL_KEYS.has(k))

  return (
    <Dialog.Root open onOpenChange={(open) => !open && close()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <Dialog.Content
          data-testid="host-modal"
          data-modal-id={modalId}
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-[440px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-card p-6 text-foreground shadow-xl"
        >
          <Dialog.Title className="mb-1 text-lg font-semibold">{title}</Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-muted-foreground">
            {description ?? 'Preview dialog — host-owned UI. Replace per modal id with a bespoke component.'}
          </Dialog.Description>

          {entries.length > 0 && (
            <dl className="mb-5 space-y-1 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs">
              {entries.map(([k, v]) => (
                <div key={k} className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="font-mono text-foreground">{String(v)}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={close}
              className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              Close
            </button>
          </div>

          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close"
              className="absolute right-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/**
 * Build the `modalId → component` registry consumed by renderer-v2's
 * `<ModalHost>`. Routes the known corpus ids to bespoke studio components
 * (contactForm / agentContactModal → ContactAgentModal, scheduleTourModal →
 * ScheduleTourModal) and falls back to the generic `HostModalBody` for ANY
 * other id (Proxy fallback), so an `openModal` for an unseen id still renders
 * something instead of silently warning. Components are memoized per id so the
 * registry returns a stable reference across renders.
 */
export function createStudioModalRegistry(): ModalComponentRegistry {
  const cache = new Map<string, ComponentType<ModalComponentProps>>()
  const componentFor = (modalId: string): ComponentType<ModalComponentProps> => {
    let component = cache.get(modalId)
    if (!component) {
      if (modalId === 'contactForm' || modalId === 'agentContactModal') {
        component = (p: ModalComponentProps) => <ContactAgentModal modalId={modalId} {...p} />
        component.displayName = `ContactAgentModal(${modalId})`
      } else if (modalId === 'scheduleTourModal') {
        component = (p: ModalComponentProps) => <ScheduleTourModal modalId={modalId} {...p} />
        component.displayName = `ScheduleTourModal(${modalId})`
      } else {
        component = (p: ModalComponentProps) => <HostModalBody modalId={modalId} {...p} />
        component.displayName = `HostModal(${modalId})`
      }
      cache.set(modalId, component)
    }
    return component
  }
  return new Proxy({} as ModalComponentRegistry, {
    get: (_target, prop) =>
      typeof prop === 'string' && !UNSAFE_MODAL_IDS.has(prop)
        ? componentFor(prop)
        : undefined,
    has: (_target, prop) =>
      typeof prop === 'string' && !UNSAFE_MODAL_IDS.has(prop),
  })
}

export interface StudioModalRegistryBundle {
  modalRegistry: ModalComponentRegistry
  adapterRef: typeof STUDIO_MODAL_REGISTRY_REF
  registryHandlers: readonly HostRegistryHandlerDeclaration[]
  wiredRegistryHandlers: readonly Pick<
    HostRegistryHandlerDeclaration,
    'capabilityId' | 'selector' | 'handlerRef'
  >[]
}

/** Runtime registry and its wildcard declaration are created as one unit. */
export function createStudioModalRegistryBundle(): StudioModalRegistryBundle {
  const modalRegistry = createStudioModalRegistry()
  const handler = {
    capabilityId: 'host.modal' as const,
    selector: '*',
    payloadSchema: { kind: 'unknown' as const },
    handlerRef: STUDIO_MODAL_REGISTRY_REF,
  }
  return {
    modalRegistry,
    adapterRef: STUDIO_MODAL_REGISTRY_REF,
    registryHandlers: [handler],
    wiredRegistryHandlers: [
      {
        capabilityId: handler.capabilityId,
        selector: handler.selector,
        handlerRef: handler.handlerRef,
      },
    ],
  }
}
